/**
 * SoleadLab 2.0 - Source File Store
 *
 * Module-scoped Map that stores original full-resolution source files
 * for later upload to R2 and tile generation. Not in Zustand because
 * File/Blob objects aren't serializable.
 *
 * Keyed by orthophoto/overlay ID.
 */

// Pixel threshold: images above this trigger tile generation
export const TILE_PIXEL_THRESHOLD = 16_000_000; // 16M pixels

// ── Large-upload ceilings ───────────────────────────────────────────────────
// Above EITHER limit the synchronous legacy-Lambda preview is skipped and the
// ortho shows a placeholder until the COG converter writes its own
// overview-derived preview (useTileStatusPoller adopts it once tiles are ready).
//
// The Lambda preview is BYTE-bound (it downloads the source from R2, then
// downsamples): a 2.6 GB source timed it out; 381 MB and a 228 MB / 619 MP
// image are both fine. So bytes are the primary cutoff. The pixel cap is only a
// decode-memory bomb guard for a small-byte / huge-pixel (extremely compressed)
// file, and even that is soft — a requestPreview failure falls back to the
// placeholder. Keep it high enough that ordinary large orthos (600-700 MP) get
// a real preview: a too-low cap gave a 228 MB manual image a useless black box
// it couldn't be positioned against.
export const SERVER_PREVIEW_MAX_BYTES = 1_000_000_000; // 1 GB
export const SERVER_PREVIEW_MAX_PIXELS = 1_500_000_000; // 1.5 GP — bomb guard only; bytes are the real cutoff

// Hard cap for the browser-side "Fast Preview" decode: readRasters' ArrayBuffer
// allocation killed an 18 GB upload (2.6 GB survives). Above this the Fast path
// refuses with guidance to use HD instead of letting the tab die.
export const FAST_DECODE_MAX_BYTES = 4_000_000_000; // 4 GB

/** True when a source is too big for the synchronous server preview. */
export function exceedsServerPreviewLimits(fileBytes: number, width: number, height: number): boolean {
  return fileBytes > SERVER_PREVIEW_MAX_BYTES || width * height > SERVER_PREVIEW_MAX_PIXELS;
}

interface SourceFileEntry {
  file: File;
  width: number;
  height: number;
}

const sourceFiles = new Map<string, SourceFileEntry>();

/** Store the original source file for later upload + tile generation */
export function storeSourceFile(layerId: string, file: File, width: number, height: number): void {
  sourceFiles.set(layerId, { file, width, height });
}

/** Get a stored source file entry */
export function getSourceFile(layerId: string): SourceFileEntry | undefined {
  return sourceFiles.get(layerId);
}

/** Remove a stored source file */
export function removeSourceFile(layerId: string): void {
  sourceFiles.delete(layerId);
}

/** Get all stored source files */
export function getAllSourceFiles(): Map<string, SourceFileEntry> {
  return sourceFiles;
}

/** Check if an image exceeds the tile threshold */
export function needsTileGeneration(width: number, height: number): boolean {
  return width * height > TILE_PIXEL_THRESHOLD;
}

/** Clear all source files (project close / clear) */
export function clearAllSourceFiles(): void {
  sourceFiles.clear();
}
